import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import { Notification, Roomgroup } from '@/models';
import { usePersistenceStore } from '@/store/persistence/persistenceStore';
import { useOrderStore } from '@/store/order/orderStore';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { useImagesStore } from '@/store/images/imageStore';
import { usePlantStore } from '@/store/entities/plantStore';
import { useRoomStore } from '@/store/entities/roomStore';
import i18n from '@/utils/i18n/order.json';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { useFormStore } from '@/store/form/formStore';
import { Order } from '@/models/Order';
import { instanceToInstance } from 'class-transformer';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';
import { Form } from '@/models/Form';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useBusinessEntityStore } from '@/store/entities/businessEntityStore';
import { getBusinessEntityUpdatedAt } from '@/api/businessEntity/BusinessEntityApi';
import envConfig from '@/config/runtimeConfig';

export function useCloseOrderButton(node: Order | undefined) {
  const persistenceStore = usePersistenceStore();
  const imagesStore = useImagesStore();
  const orderStore = useOrderStore();
  const notificationStore = useNotificationStore();
  const router = useRouter();

  const modalOpen = ref(false);
  const order = ref(new Order());

  const initializeOrderForm = () => {
    order.value = instanceToInstance(node);
  };

  const isOnline = computed(() => useLayoutStore().isOnline);

  const isOnSiteInspection = computed(() => {
    return useOrderStore().isOnSiteInspection();
  });

  const isFirstInstallation = computed(() => {
    return orderStore.activeOrder?.detail?.isFirstInstallation;
  });

  const isCompletedInspectionOrder = computed(() => {
    if (!isOnSiteInspection.value) return false;
    return (
      areBuildingsNotCompatible.value ||
      ((isPlantInstalled.value || hasNotAccessibleHeaterRooms.value) &&
        hasSelectedLocation.value &&
        !hasMissingLocationData.value)
    );
  });

  const isPlantInstalled = computed(() => {
    return usePlantStore().plants.size > 0;
  });

  const hasNotAccessibleHeaterRooms = computed(() => {
    return useRoomStore().getNotAccessibleHeaterRooms().length > 0;
  });

  const areBuildingsNotCompatible = computed(() => {
    return useInspectedBuildingStore().areAllBuildingsNotCompatible();
  });

  const isSmartHeatingOrderCompleted = computed(() => {
    return (
      isFirstInstallation.value &&
      unfinishedSmartHeatingRoomGroups.value?.size === 0
    );
  });

  const hasSelectedLocation = computed(() => {
    return useInspectionPointStore().getSelectedLocations().length > 0;
  });

  const getLocationWithMissingData = computed(() => {
    return useInspectionPointStore().getLocationWithMissingData();
  });

  const hasMissingLocationData = computed(() => {
    return getLocationWithMissingData.value.length > 0;
  });

  const unfinishedSmartHeatingRoomGroups = computed(() => {
    return isFirstInstallation.value
      ? orderStore.unfinishedSmartHeatingRoomGroups()
      : new Map<string, Roomgroup>();
  });

  const promptData = computed(() => {
    if (isOnSiteInspection.value && !hasSelectedLocation.value) {
      return {
        icon: Notification.WARNING,
        title: i18n.closeOrderPrompt.title.firstInstallation.incomplete,
        question: i18n.closeOrderPrompt.body.hasInstallationLocation.incomplete,
        proceedText: i18n.closeOrderPrompt.buttons.default.proceed,
        cancelText: i18n.closeOrderPrompt.buttons.default.cancel,
      };
    }
    if (
      isOnSiteInspection.value &&
      hasSelectedLocation.value &&
      hasMissingLocationData.value
    ) {
      return {
        icon: Notification.WARNING,
        title: i18n.closeOrderPrompt.title.firstInstallation.incomplete,
        question:
          i18n.closeOrderPrompt.body.hasInstallationLocation.incompleteData,
        proceedText:
          i18n.closeOrderPrompt.buttons.firstInstallation.proceed.incomplete,
        cancelText: i18n.closeOrderPrompt.buttons.default.cancel,
      };
    }
    if (isOnSiteInspection.value && !isCompletedInspectionOrder.value) {
      return {
        icon: Notification.ERROR,
        title: i18n.closeOrderPrompt.title.firstInstallation.incomplete,
        question: i18n.closeOrderPrompt.body.onSiteInspection.incomplete,
        proceedText:
          i18n.closeOrderPrompt.buttons.firstInstallation.proceed.incomplete,
        cancelText: i18n.closeOrderPrompt.buttons.default.cancel,
      };
    }
    if (!isFirstInstallation.value) {
      return {
        title: i18n.closeOrderPrompt.title.default,
        question: i18n.closeOrderPrompt.body.default,
        proceedText: i18n.closeOrderPrompt.buttons.default.proceed,
        cancelText: i18n.closeOrderPrompt.buttons.default.cancel,
      };
    }
    if (isSmartHeatingOrderCompleted.value) {
      return {
        icon: Notification.SUCCESS,
        title: i18n.closeOrderPrompt.title.firstInstallation.completed,
        question: i18n.closeOrderPrompt.body.firstInstallation.completed,
        proceedText:
          i18n.closeOrderPrompt.buttons.firstInstallation.proceed.completed,
        cancelText:
          i18n.closeOrderPrompt.buttons.firstInstallation.cancel.completed,
      };
    } else {
      return {
        icon: Notification.ERROR,
        title: i18n.closeOrderPrompt.title.firstInstallation.incomplete,
        question: i18n.closeOrderPrompt.body.firstInstallation.incomplete,
        proceedText:
          i18n.closeOrderPrompt.buttons.firstInstallation.proceed.incomplete,
        cancelText:
          i18n.closeOrderPrompt.buttons.firstInstallation.cancel.incomplete,
      };
    }
  });

  const getDisplayName = (roomGroup: Roomgroup) => {
    const room = useRoomStore().getFirstRoomOfRoomgroup(roomGroup.id);
    const building = useBuildingStore().getCurrentBuilding();
    return (
      room?.getFloor() +
      ' ' +
      roomGroup.doorbellName +
      ', ' +
      building?.shortAddress
    );
  };

  const getLocationDisplayName = (inspectionPoint: InspectionPoint) => {
    const building = useBuildingStore().getBuildingByRoomId(
      inspectionPoint.roomId
    );
    const room = useRoomStore().getRoomById(inspectionPoint.roomId);
    const roomGroupId = room.roomGroupId;
    if (!roomGroupId) return '';
    const rgLabel = useRoomGroupStore()
      .getRoomGroupById(roomGroupId)
      .getLabel();
    return building?.shortAddress + ' - ' + room?.getFloor() + ', ' + rgLabel;
  };

  const openDialog = () => {
    console.log('isCompletedInspectionOrder', isCompletedInspectionOrder.value);
    return isCompletedInspectionOrder.value ? openOrderForm() : toggleModal();
  };

  const openOrderForm = () => {
    const layoutStore = useLayoutStore();
    layoutStore.setOrderFormState(true);
    const businessEntityId = useEntitiesStore().activeBusinessEntityId;
    if (!businessEntityId) return;
    useFormStore().setCurrentForm(
      new Form().setName('order').setParentId(businessEntityId)
    );
  };

  const toggleModal = () => {
    modalOpen.value = !modalOpen.value;
    if (modalOpen.value) {
      orderStore.setOrderConfirmationDialogDisplayed(true);
    }
  };

  const onProceed = () => {
    if (
      (!isFirstInstallation.value || isSmartHeatingOrderCompleted.value) &&
      !isOnSiteInspection.value
    ) {
      finishOrder();
    } else {
      toggleModal();
    }
  };

  const finishOrder = async () => {
    const layoutStore = useLayoutStore();
    layoutStore.setLoadingState(true);

    const saveSuccessful = await startSaveProcess();

    if (!saveSuccessful) {
      console.error('Save process failed. Aborting finishOrder.');
      layoutStore.resolveLoaderState(
        'Speichern fehlgeschlagen. Auftrag konnte nicht abgeschlossen werden.',
        false
      );
      setTimeout(() => {
        layoutStore.setLoadingState(false);
        persistenceStore.setIsSaving(false);
        toggleModal();
      }, 1500);
      return;
    }

    try {
      await orderStore.finishOrder(
        order.value.detail?.note,
        order.value.propertyCompatibility
      );

      layoutStore.resolveLoaderState(
        'Auftrag erfolgreich abgeschlossen...',
        true
      );
      layoutStore.setOrderFormState(false);
      setTimeout(() => {
        layoutStore.setLoadingState(false);
        router.push({ name: 'Home' });
      }, 1500);
    } catch (error) {
      console.error('Error finishing order:', error);
      layoutStore.resolveLoaderState(
        'Fehler beim Abschluss des Auftrags...',
        false
      );
      setTimeout(() => {
        layoutStore.setLoadingState(false);
        persistenceStore.setIsSaving(false);
        toggleModal();
      }, 1500);
    }
  };

  // DEV/QA ONLY — conflict detection for LIST mode. NOT for production use.
  const isDevOrQaListMode = computed(() => {
    const isNonProdEnv =
      envConfig.sentryEnv === 'dev' || envConfig.sentryEnv === 'qa';
    const isListMode = useLayoutStore().workMode === WorkMode.LIST;
    return isNonProdEnv && isListMode;
  });

  const checkForConflicts_devQaOnly = async (): Promise<boolean> => {
    if (!isDevOrQaListMode.value) return true;

    const businessEntity = useBusinessEntityStore().businessEntity;
    if (!businessEntity?.id) return true;

    const localUpdatedAt = businessEntity.updatedAt;
    if (!localUpdatedAt) return true;

    try {
      const remoteUpdatedAt = await getBusinessEntityUpdatedAt(
        businessEntity.id
      );
      if (!remoteUpdatedAt || remoteUpdatedAt === localUpdatedAt) return true;

      return window.confirm(
        `[DEV/QA ONLY] Conflict detected!\n\n` +
          `Local updatedAt: ${localUpdatedAt}\n` +
          `Remote updatedAt: ${remoteUpdatedAt}\n\n` +
          `Someone else may have saved changes. Do you want to overwrite?`
      );
    } catch (error) {
      console.warn('Conflict check failed, proceeding with save:', error);
      return true;
    }
  };

  const startSaveProcess = async (): Promise<boolean> => {
    if (!isOnline.value) {
      notificationStore.addNotification(
        new Notification(
          'Stell sicher, dass du mit dem Internet verbunden bist.',
          'Du bist offline!',
          'error'
        )
      );
      return false;
    }

    const shouldProceed = await checkForConflicts_devQaOnly();
    if (!shouldProceed) return false;

    persistenceStore.setIsSaving(true);

    try {
      await persistenceStore.saveData();
      await imagesStore.uploadImages();

      notificationStore.addNotification(
        new Notification(
          'Daten wurden erfolgreich gespeichert.',
          'Speichern erfolgreich',
          'info',
          8000
        )
      );
      return true;
    } catch (error) {
      console.error('Error saving data', error);
      notificationStore.addNotification(
        new Notification(
          'Es gab einen Fehler beim Speichern der Daten.',
          'Speichern fehlgeschlagen',
          'error',
          10000
        )
      );
      return false;
    } finally {
      persistenceStore.setIsSaving(false);
    }
  };

  return {
    isOnline,
    modalOpen,
    order,
    startSaveProcess,
    toggleModal,
    unfinishedSmartHeatingRoomGroups,
    isSmartHeatingOrderCompleted,
    hasMissingLocationData,
    promptData,
    getLocationWithMissingData,
    openDialog,
    onProceed,
    getDisplayName,
    getLocationDisplayName,
    finishOrder,
    initializeOrderForm,
  };
}
